/**
 * DecisionsPage
 *
 * Workspace decision log: manual + auto decisions recorded in the system.
 * Supports create, view, edit (title/body/class/impact/status/tags/related), delete.
 *
 * Routes:
 *   /prioritization/decisions
 *
 * Canon: DOCS_V4/00-canon/12-decision-recording-spec.v1.md
 */

import { Fragment, useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AgentBar } from '../../components/agents/AgentBar';
import { stakeholderPulseAgent } from '../../components/agents/contextualAgents';
import {
  GitBranch,
  Plus,
  Search,
  X,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Sparkles,
  User,
  Calendar,
  Tag,
  MoreHorizontal,
  Check,
  AlertTriangle,
  Trash2,
  Link2,
  Layers,
  RefreshCcw,
  Target,
  FileText,
  ChevronRight,
  Edit2,
  Boxes,
  BookOpen,
  Network,
  List,
  Copy,
} from 'lucide-react';
import { DecisionMapView } from '../../components/prioritization/decisions/DecisionMapView';
import {
  decisionDateInputToIso,
  decisionDateInputValue,
  decisionPagePath,
} from '../../components/prioritization/decisions/decisionRecordText';
import { RationalePanel } from '../../components/prioritization/RationalePanel';
import { ItemPoolCombobox } from '../../components/prioritization/item-list/components/ItemPoolCombobox';
import { SearchableSelect } from '../../components/shared/SearchableSelect';
import { Select } from '../../components/ds';
import { parentDecisionCandidates } from '../../lib/decisionParent';
import { usePrioritySetStore } from '../../stores/prioritization/prioritySetStore';
import { recordRejectedAlternatives } from '../../lib/rejectedAlternativesService';
import type { ComparisonSetKind } from '../../lib/rejectedAlternatives';
import { HEALTH_CONFIG } from '../../lib/decisionHealth';
import type { DecisionHealthStatus } from '../../types/prioritization/decisions';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { useDecisionsStore } from '../../stores/prioritization/decisionsStore';
import { decisionRelatedKindLabel, useDecisionRelatedNames } from '../../components/prioritization/decisions/useDecisionRelatedNames';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useSessionStore } from '../../stores/prioritization/sessionStore';
import { useDegapProcessStore } from '../../stores/prioritization/degapProcessStore';
import { useTrackStore } from '../../stores/prioritization/trackStore';
import { useActorDisplayMap } from '../../hooks/prioritization/useActorDisplayMap';
import { useSpaceStore } from '../../stores/prioritization/spaceStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useUIStore } from '../../stores/prioritization/uiStore';
import type {
  Decision,
  DecisionImpact,
  DecisionRelatedObject,
  DecisionRelatedObjectKind,
  DecisionClass,
  CreateDecisionPayload,
  UpdateDecisionPayload,
} from '../../types/prioritization/decisions';
import {
  ATTACHABLE_DECISION_RELATED_OBJECT_KINDS,
  DECISION_RELATED_OBJECT_KIND_LABELS,
  decisionRelatedObjectHref,
  DECISION_CLASS_LABELS,
  DECISION_CLASS_COLORS,
  DECISION_CLASS_OPTIONS,
} from '../../types/prioritization/decisions';

// ============================================================================
// Constants
// ============================================================================

const IMPACT_CONFIG: Record<DecisionImpact, { label: string; color: string }> = {
  high:   { label: 'High',   color: 'bg-[var(--coral-dim)] text-[var(--coral)]' },
  medium: { label: 'Medium', color: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
  low:    { label: 'Low',    color: 'bg-[var(--hover-tint)] text-[var(--text-sec)]' },
};

const STATUS_CONFIG: Record<Decision['status'], { label: string; color: string }> = {
  active:     { label: 'Active',     color: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' },
  superseded: { label: 'Superseded', color: 'bg-[var(--hover-tint)] text-[var(--text-ter)]' },
  reversed:   { label: 'Reversed',   color: 'bg-[var(--coral-dim)] text-[var(--coral)]' },
};

const KIND_ICON: Record<DecisionRelatedObjectKind, React.ElementType> = {
  item:    FileText,
  session: Target,
  cycle:   RefreshCcw,
  track:   Layers,
  plan_cycle:              BookOpen,
  plan_version:            BookOpen,
  plan_of_record:          BookOpen,
  plan_of_record_snapshot: BookOpen,
};

// ============================================================================
// Helpers
// ============================================================================

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  }).format(new Date(iso));
}

function relativeTime(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 86400 * 7) return `${Math.floor(seconds / 86400)}d ago`;
  return formatDate(iso);
}


function decisionShareUrl(decisionId: string): string {
  // The canonical per-decision URL — the decisions/:id route resolves it to
  // the map view with this decision selected.
  return new URL(`/prioritization/decisions/${decisionId}`, window.location.origin).toString();
}

// ============================================================================
// Sort helpers
// ============================================================================

type SortKey = 'decidedAt' | 'title' | 'impact' | 'status' | 'decisionKind';
type SortDir = 'asc' | 'desc';

function sortDecisions(items: Decision[], key: SortKey, dir: SortDir): Decision[] {
  return [...items].sort((a, b) => {
    let av: string, bv: string;
    if (key === 'decidedAt') {
      av = a.decidedAt;
      bv = b.decidedAt;
    } else if (key === 'impact') {
      const order = { high: 0, medium: 1, low: 2 };
      av = String(order[a.impact]);
      bv = String(order[b.impact]);
    } else {
      av = String(a[key] ?? '').toLowerCase();
      bv = String(b[key] ?? '').toLowerCase();
    }
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return dir === 'asc' ? cmp : -cmp;
  });
}

// ============================================================================
// Related-object name resolution hook
// ============================================================================

interface ResolvedObject extends DecisionRelatedObject {
  name: string;
  href: string;
}

function useResolvedObjects(decisions: Decision[]): Record<string, ResolvedObject[]> {
  const { nameFor } = useDecisionRelatedNames();

  return useMemo(() => {
    const out: Record<string, ResolvedObject[]> = {};

    for (const d of decisions) {
      const refs: ResolvedObject[] = [];

      for (const ro of d.relatedObjects ?? []) {
        // related_objects is also written by the database (788, 797), so the
        // set of kinds is open: anything without a page of its own — a plan
        // version or snapshot, or a kind this client does not know — is left
        // out of the pills rather than rendered with no icon and no link.
        const href = decisionRelatedObjectHref(ro);
        if (!href || !KIND_ICON[ro.kind]) continue;
        const name = nameFor(ro) ?? decisionRelatedKindLabel(ro.kind);
        refs.push({ ...ro, name, href });
      }

      // Surface legacy single-FK columns as pills if set and not already in relatedObjects
      if (d.itemId && !refs.some((r) => r.kind === 'item' && r.id === d.itemId)) {
        const name = nameFor({ kind: 'item', id: d.itemId }) ?? d.itemTitle ?? decisionRelatedKindLabel('item');
        refs.push({ kind: 'item', id: d.itemId, name, href: `/prioritization/items/${d.itemId}` });
      }

      if (refs.length > 0) out[d.id] = refs;
    }

    return out;
  }, [decisions, nameFor]);
}

// ============================================================================
// Object attachment picker
// ============================================================================

type AttachableKind = (typeof ATTACHABLE_DECISION_RELATED_OBJECT_KINDS)[number];
const KINDS: readonly AttachableKind[] = ATTACHABLE_DECISION_RELATED_OBJECT_KINDS;

function ObjectAttachmentPicker({
  selected,
  onChange,
}: {
  selected: DecisionRelatedObject[];
  onChange: (next: DecisionRelatedObject[]) => void;
}) {
  const [activeKind, setActiveKind] = useState<AttachableKind>('item');
  const [query, setQuery] = useState('');

  const items    = useItemsStore((s) => s.items);
  const sessions = useSessionStore((s) => s.sessions);
  const cycles   = useDegapProcessStore((s) => s.cycles);
  const tracks   = useTrackStore((s) => s.tracks);

  type Option = { id: string; name: string };

  const options = useMemo((): Option[] => {
    const q = query.toLowerCase();
    switch (activeKind) {
      case 'item':
        return items.filter((i) => i.title?.toLowerCase().includes(q)).slice(0, 8).map((i) => ({ id: i.id, name: i.title }));
      case 'session':
        return sessions.filter((s) => s.name?.toLowerCase().includes(q)).slice(0, 8).map((s) => ({ id: s.id, name: s.name }));
      case 'cycle':
        return cycles.filter((c) => c.name?.toLowerCase().includes(q)).slice(0, 8).map((c) => ({ id: c.id, name: c.name }));
      case 'track':
        return tracks.filter((t) => t.name?.toLowerCase().includes(q)).slice(0, 8).map((t) => ({ id: t.id, name: t.name }));
    }
  }, [activeKind, query, items, sessions, cycles, tracks]);

  const isSelected = (kind: DecisionRelatedObjectKind, id: string) =>
    selected.some((s) => s.kind === kind && s.id === id);

  const toggle = (kind: DecisionRelatedObjectKind, id: string) => {
    if (isSelected(kind, id)) {
      onChange(selected.filter((s) => !(s.kind === kind && s.id === id)));
    } else {
      onChange([...selected, { kind, id }]);
    }
  };

  return (
    <div>
      <div className="flex gap-1 mb-2 flex-wrap">
        {KINDS.map((k) => {
          const Icon = KIND_ICON[k];
          const count = selected.filter((s) => s.kind === k).length;
          return (
            <button
              key={k}
              type="button"
              onClick={() => { setActiveKind(k); setQuery(''); }}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                activeKind === k ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' : 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
              }`}
            >
              <Icon className="h-3 w-3" />
              {DECISION_RELATED_OBJECT_KIND_LABELS[k]}
              {count > 0 && (
                <span className="ml-0.5 bg-[var(--slate)] text-white rounded-full w-4 h-4 text-xs flex items-center justify-center leading-none">
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div className="relative mb-2">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${DECISION_RELATED_OBJECT_KIND_LABELS[activeKind].toLowerCase()}s…`}
          className="w-full pl-8 pr-3 py-1.5 border border-[var(--rule)] text-xs focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-[var(--slate)]"
        />
      </div>
      <div className="max-h-36 overflow-y-auto border border-[var(--rule)] divide-y divide-[var(--rule)]">
        {options.length === 0 ? (
          <p className="px-3 py-3 text-xs text-[var(--text-ter)]">
            {query ? 'No matches' : `No ${DECISION_RELATED_OBJECT_KIND_LABELS[activeKind].toLowerCase()}s found`}
          </p>
        ) : (
          options.map((opt) => {
            const sel = isSelected(activeKind, opt.id);
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => toggle(activeKind, opt.id)}
                className={`w-full flex items-center justify-between px-3 py-2 text-xs text-left transition-colors ${
                  sel ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' : 'hover:bg-[var(--surface)] text-[var(--text-sec)]'
                }`}
              >
                <span className="truncate">{opt.name}</span>
                {sel && <Check className="h-3 w-3 flex-shrink-0 ml-2 text-[var(--text-ter)]" />}
              </button>
            );
          })
        )}
      </div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selected.map((obj) => {
            const Icon = KIND_ICON[obj.kind];
            const label = options.find((o) => o.id === obj.id)?.name ??
              (() => {
                if (obj.kind === 'item')    return items.find((i) => i.id === obj.id)?.title;
                if (obj.kind === 'session') return sessions.find((s) => s.id === obj.id)?.name;
                if (obj.kind === 'cycle')   return cycles.find((c) => c.id === obj.id)?.name;
                if (obj.kind === 'track')   return tracks.find((t) => t.id === obj.id)?.name;
              })() ?? obj.id.slice(0, 8);
            return (
              <span
                key={`${obj.kind}:${obj.id}`}
                className="inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 bg-[var(--slate-dim)] text-[var(--text-sec)] text-xs rounded border border-[var(--slate)]"
              >
                <Icon className="h-3 w-3 opacity-70" />
                <span className="max-w-[120px] truncate">{label}</span>
                <button type="button" onClick={() => toggle(obj.kind, obj.id)} className="ml-0.5 hover:text-[var(--text-ter)] transition-colors">
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Column header
// ============================================================================

function SortableHeader({
  label, sortKey, currentKey, dir, onSort,
}: {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey;
  dir: SortDir;
  onSort: (k: SortKey) => void;
}) {
  const active = currentKey === sortKey;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className="flex items-center gap-1 text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide hover:text-[var(--text-pri)] transition-colors select-none"
    >
      {label}
      {active
        ? dir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
        : <ChevronsUpDown className="h-3 w-3 opacity-40" />}
    </button>
  );
}

// ============================================================================
// Row actions menu
// ============================================================================

/**
 * A decision's memorialized content is editable only while it is a live,
 * human-authored record. Auto decisions and settled (superseded/reversed)
 * decisions are immutable — revision goes through supersession (canon 27 §6;
 * enforced by migration 573).
 */
function isContentEditable(d: Decision): boolean {
  return d.decisionKind === 'manual' && d.status === 'active';
}

function RowMenu({
  decision,
  onDelete,
  onEdit,
  onSupersede,
  onCopyLink,
}: {
  decision: Decision;
  onDelete: (id: string) => void;
  onEdit: (d: Decision) => void;
  onSupersede: (d: Decision) => void;
  onCopyLink: (d: Decision) => void;
}) {
  const [open, setOpen] = useState(false);
  const updateDecision = useDecisionsStore((s) => s.updateDecision);
  const addNotification = useUIStore((s) => s.addNotification);

  const setStatus = async (status: Decision['status']) => {
    setOpen(false);
    const updated = await updateDecision(decision.id, { status });
    if (!updated) {
      addNotification({
        type: 'error',
        title: 'Status was not changed',
        message: useDecisionsStore.getState().lastError ?? 'Try again.',
        duration: 7000,
      });
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="p-1 rounded hover:bg-[var(--hover-tint)] text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-1 w-48 bg-[var(--bg-raised)] border border-[var(--rule)] shadow-lg py-1 text-sm">
            <button
              type="button"
              onClick={() => { setOpen(false); onCopyLink(decision); }}
              className="w-full text-left px-3 py-1.5 hover:bg-[var(--surface)] text-[var(--text-sec)] flex items-center gap-2"
            >
              <Copy className="h-3.5 w-3.5 text-[var(--text-ter)]" />
              Copy link
            </button>
            {isContentEditable(decision) && (
              <button
                type="button"
                onClick={() => { setOpen(false); onEdit(decision); }}
                className="w-full text-left px-3 py-1.5 hover:bg-[var(--surface)] text-[var(--text-sec)] flex items-center gap-2"
              >
                <Edit2 className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                Edit
              </button>
            )}
            {decision.status === 'active' && (
              <button
                type="button"
                onClick={() => { setOpen(false); onSupersede(decision); }}
                className="w-full text-left px-3 py-1.5 hover:bg-[var(--surface)] text-[var(--text-sec)] flex items-center gap-2"
              >
                <RefreshCcw className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                Supersede…
              </button>
            )}
            <div className="border-t border-[var(--rule)] my-1" />
            {decision.status !== 'superseded' && (
              <button type="button" onClick={() => setStatus('superseded')} className="w-full text-left px-3 py-1.5 hover:bg-[var(--surface)] text-[var(--text-sec)] flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                Mark superseded
              </button>
            )}
            {decision.status !== 'reversed' && (
              <button type="button" onClick={() => setStatus('reversed')} className="w-full text-left px-3 py-1.5 hover:bg-[var(--surface)] text-[var(--text-sec)] flex items-center gap-2">
                <AlertTriangle className="h-3.5 w-3.5 text-[var(--mustard)]" />
                Mark reversed
              </button>
            )}
            {decision.status !== 'active' && (
              <button type="button" onClick={() => setStatus('active')} className="w-full text-left px-3 py-1.5 hover:bg-[var(--surface)] text-[var(--text-sec)] flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-[var(--green)]" />
                Mark active
              </button>
            )}
            {/* delete_decision (mig 765): only a manual decision still in force —
                settled ones are memorialized (canon 27 §2). */}
            {decision.decisionKind === 'manual' && decision.status === 'active' && (
              <>
                <div className="border-t border-[var(--rule)] my-1" />
                <button
                  type="button"
                  onClick={() => { setOpen(false); onDelete(decision.id); }}
                  className="w-full text-left px-3 py-1.5 hover:bg-[var(--coral-dim)] text-[var(--coral)] flex items-center gap-2"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Create / Edit Decision slide-over
// ============================================================================

const IMPACT_OPTIONS: DecisionImpact[] = ['high', 'medium', 'low'];

interface SlideOverProps {
  open: boolean;
  onClose: () => void;
  /** When editing: existing decision; null for create */
  editing?: Decision | null;
  /** When superseding: the decision the new one replaces (canon 27 §6). */
  superseding?: Decision | null;
  onCreate?: (payload: CreateDecisionPayload, alternatives?: { kind: ComparisonSetKind; id: string }) => Promise<void>;
  onUpdate?: (id: string, payload: UpdateDecisionPayload) => Promise<void>;
  /** Pre-attach related objects on a fresh create (e.g. from a finding's "Record a decision" link). */
  initialRelatedObjects?: DecisionRelatedObject[];
}

function DecisionSlideOver({ open, onClose, editing, superseding, onCreate, onUpdate, initialRelatedObjects }: SlideOverProps) {
  const isEdit = !!editing;

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [impact, setImpact] = useState<DecisionImpact>('medium');
  const [decisionClass, setDecisionClass] = useState<DecisionClass | ''>('');
  const [tagsInput, setTagsInput] = useState('');
  const [decidedAt, setDecidedAt] = useState('');
  const [relatedObjects, setRelatedObjects] = useState<DecisionRelatedObject[]>([]);
  // "Part of": the larger decision this one belongs to — the branch the map draws.
  const [parentDecisionId, setParentDecisionId] = useState('');
  const allDecisions = useDecisionsStore((s) => s.decisions);
  const parentCandidates = useMemo(
    () => parentDecisionCandidates(allDecisions, editing?.id ?? null),
    [allDecisions, editing?.id],
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Structural alternatives (P2, migration 702) — only offered on a fresh
  // create, never on edit/supersede. 'none' = don't record any.
  const [altKind, setAltKind] = useState<ComparisonSetKind | 'none'>('none');
  const [altSetId, setAltSetId] = useState('');
  const prioritySets = usePrioritySetStore((s) => s.prioritySets);

  // Seed form from decision when editing; inherit context when superseding.
  useEffect(() => {
    if (editing) {
      setTitle(editing.title);
      setBody(editing.body ?? '');
      setImpact(editing.impact);
      setDecisionClass(editing.decisionClass ?? '');
      setTagsInput(editing.tags.join(', '));
      setDecidedAt(decisionDateInputValue(editing.decidedAt));
      setRelatedObjects(editing.relatedObjects ?? []);
      setParentDecisionId(editing.parentDecisionId ?? '');
    } else if (superseding) {
      // A superseding decision is a NEW choice on the same subject: inherit
      // classification and attachments, never the old title/body.
      setTitle('');
      setBody('');
      setImpact(superseding.impact);
      setDecisionClass(superseding.decisionClass ?? '');
      setTagsInput(superseding.tags.join(', '));
      setDecidedAt('');
      setRelatedObjects(superseding.relatedObjects ?? []);
      setParentDecisionId(superseding.parentDecisionId ?? '');
    } else {
      setTitle('');
      setBody('');
      setImpact('medium');
      setDecisionClass('');
      setTagsInput('');
      setDecidedAt('');
      setRelatedObjects(initialRelatedObjects ?? []);
      setParentDecisionId('');
    }
    setAltKind('none');
    setAltSetId('');
    setError(null);
  }, [editing, superseding, open, initialRelatedObjects]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { setError('Title is required.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const tags = tagsInput.split(',').map((t) => t.trim()).filter(Boolean);
      const classVal = (decisionClass as DecisionClass) || undefined;

      if (isEdit && onUpdate && editing) {
        await onUpdate(editing.id, {
          title: title.trim(),
          body: body.trim() || undefined,
          impact,
          decisionClass: classVal ?? null,
          tags,
          relatedObjects: relatedObjects.length > 0 ? relatedObjects : [],
          // An edit that leaves the day alone leaves the moment alone: the
          // date is rewritten only when the person picked a different day.
          decidedAt:
            decidedAt && decidedAt !== decisionDateInputValue(editing.decidedAt)
              ? decisionDateInputToIso(decidedAt)
              : undefined,
          parentDecisionId: parentDecisionId || null,
        });
      } else if (!isEdit && onCreate) {
        await onCreate(
          {
            title: title.trim(),
            body: body.trim() || undefined,
            impact,
            decisionClass: classVal,
            tags,
            relatedObjects: relatedObjects.length > 0 ? relatedObjects : undefined,
            decidedAt: decidedAt ? decisionDateInputToIso(decidedAt) : undefined,
            supersedesDecisionId: superseding?.id,
            subjectKind: superseding?.subjectKind ?? undefined,
            parentDecisionId: parentDecisionId || undefined,
          },
          altKind !== 'none' && altSetId ? { kind: altKind, id: altSetId } : undefined,
        );
      }
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save decision.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="fixed inset-0 bg-black/20" onClick={handleClose} />
      <div className="relative z-50 w-full max-w-md bg-[var(--bg-raised)] shadow-xl flex flex-col h-full overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <div className="flex items-center gap-2">
            <GitBranch className="h-4 w-4 text-[var(--text-ter)]" />
            <h2 className="text-base font-semibold text-[var(--text-pri)]">
              {isEdit ? 'Edit Decision' : superseding ? 'Supersede Decision' : 'Record Decision'}
            </h2>
          </div>
          <button type="button" onClick={handleClose} className="p-1 hover:bg-[var(--hover-tint)] rounded">
            <X className="h-4 w-4 text-[var(--text-ter)]" />
          </button>
        </div>

        {superseding && (
          <div className="px-6 py-3 bg-[var(--slate-dim)] border-b border-[var(--slate)] text-sm text-[var(--text-sec)]">
            <span className="font-medium">Supersedes:</span> {superseding.title}
            <p className="text-xs text-[var(--text-sec)] mt-0.5">
              The original stays on the record, marked superseded and linked to this new decision.
            </p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-5 p-6 flex-1">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Decision title <span className="text-[var(--coral)]">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. We will prioritize security over new features this quarter"
              className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-[var(--slate)]"
            />
          </div>

          {/* Body */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Context / rationale
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              placeholder="Why was this decision made? What alternatives were considered?"
              className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-[var(--slate)] resize-none"
            />
          </div>

          {/* Decision class + impact row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Decision class
              </label>
              <select
                value={decisionClass}
                onChange={(e) => setDecisionClass(e.target.value as DecisionClass | '')}
                className="w-full px-3 py-2 border border-[var(--rule)] text-sm bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-[var(--slate)]"
              >
                <option value="">— none —</option>
                {DECISION_CLASS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Impact</label>
              <div className="flex gap-2">
                {IMPACT_OPTIONS.map((imp) => {
                  const cfg = IMPACT_CONFIG[imp];
                  return (
                    <button
                      key={imp}
                      type="button"
                      onClick={() => setImpact(imp)}
                      className={`flex-1 px-2 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        impact === imp
                          ? `${cfg.color} border-transparent ring-2 ring-offset-1 ring-violet-400`
                          : 'bg-[var(--surface)] text-[var(--text-ter)] border-[var(--rule)] hover:bg-[var(--hover-tint)]'
                      }`}
                    >
                      {cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Decided at */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1 flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 text-[var(--text-ter)]" />
              Decided on
              <span className="text-xs text-[var(--text-ter)] font-normal">(defaults to today)</span>
            </label>
            <input
              type="date"
              value={decidedAt}
              onChange={(e) => setDecidedAt(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-[var(--slate)]"
            />
          </div>

          {/* Related objects */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2 flex items-center gap-1.5">
              <Link2 className="h-3.5 w-3.5 text-[var(--text-ter)]" />
              Related to
              <span className="text-xs text-[var(--text-ter)] font-normal">(optional)</span>
            </label>
            <ObjectAttachmentPicker selected={relatedObjects} onChange={setRelatedObjects} />
          </div>

          {/* Part of — the larger decision this one belongs to. The map draws
              this as a branch; without it every record is its own root. */}
          <div>
            <label htmlFor="decision-parent" className="block text-sm font-medium text-[var(--text-sec)] mb-2">
              Part of a larger decision
              <span className="ml-1.5 text-xs text-[var(--text-ter)] font-normal">(optional)</span>
            </label>
            <Select
              id="decision-parent"
              value={parentDecisionId}
              onChange={(e) => setParentDecisionId(e.target.value)}
              options={[{ value: '', label: 'None — this decision stands on its own' }, ...parentCandidates.map((d) => ({ value: d.id, label: d.title }))]}
            />
            <p className="mt-1 text-xs text-[var(--text-ter)]">
              A sub-decision inherits nothing; it is drawn under the decision it belongs to.
            </p>
          </div>

          {/* Structural alternatives (P2, migration 702) — record_rejected_alternatives
              derives the below-cutline items from the chosen set itself; this only
              picks WHICH locked set the decision was made against. */}
          {!isEdit && (
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-2 flex items-center gap-1.5">
                <Layers className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                Alternatives considered and rejected
                <span className="text-xs text-[var(--text-ter)] font-normal">(optional)</span>
              </label>
              <p className="text-xs text-[var(--text-ter)] mb-2">
                Pick the locked comparison set this decision was made against. Its
                below-the-cut-line items are recorded as structural alternatives —
                what this decision beat, not what it says it beat.
              </p>
              <div className="flex gap-2">
                <Select
                  value={altKind}
                  onChange={(e) => { setAltKind(e.target.value as ComparisonSetKind | 'none'); setAltSetId(''); }}
                  className="w-36 flex-shrink-0"
                >
                  <option value="none">None</option>
                  <option value="item_pool">Item pool</option>
                  <option value="priority_set">Priority list</option>
                </Select>
                {altKind === 'item_pool' && (
                  <ItemPoolCombobox value={altSetId} onChange={setAltSetId} className="flex-1" />
                )}
                {altKind === 'priority_set' && (
                  <SearchableSelect
                    options={prioritySets
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((s) => ({ value: s.id, label: `${s.name}${s.cutLinePosition != null ? '' : ' (no cut line yet)'}` }))}
                    value={altSetId}
                    onChange={setAltSetId}
                    placeholder="Select a priority list…"
                    className="flex-1"
                  />
                )}
              </div>
            </div>
          )}

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Tags <span className="text-xs text-[var(--text-ter)] font-normal">(comma-separated)</span>
            </label>
            <input
              type="text"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="e.g. security, q2-2026, leadership"
              className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-[var(--slate)]"
            />
          </div>

          {error && <p className="text-sm text-[var(--coral)]">{error}</p>}

          <div className="flex gap-2 mt-auto pt-4 border-t border-[var(--rule)]">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-[var(--rule)] text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !title.trim()}
              className="flex-1 px-4 py-2 bg-[var(--slate)] text-white text-sm font-medium hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Record Decision'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// Inline expanded row detail
// ============================================================================

function ExpandedRow({
  decision,
  resolvedObjects,
  actorName,
  onEdit,
  onCopyLink,
}: {
  decision: Decision;
  resolvedObjects: ResolvedObject[];
  actorName: string | null;
  onEdit: (d: Decision) => void;
  onCopyLink: (d: Decision) => void;
}) {
  return (
    <tr>
      <td colSpan={7} className="px-4 pb-4 bg-[var(--slate-dim)]/40 border-b border-[var(--slate)]">
        <div className="pt-3 flex flex-col gap-3">
          {/* Body / rationale */}
          {decision.body ? (
            <div>
              <div className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mb-1">Rationale</div>
              <p className="text-sm text-[var(--text-sec)] whitespace-pre-line leading-relaxed">{decision.body}</p>
            </div>
          ) : (
            <p className="text-xs text-[var(--text-ter)] italic">No rationale recorded.</p>
          )}

          {/* Metadata row */}
          <div className="flex flex-wrap gap-4 text-xs text-[var(--text-ter)]">
            {decision.decisionClass && (
              <span className="flex items-center gap-1">
                <Boxes className="h-3 w-3" />
                Class: <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-medium ${DECISION_CLASS_COLORS[decision.decisionClass]}`}>
                  {DECISION_CLASS_LABELS[decision.decisionClass]}
                </span>
              </span>
            )}
            {actorName && (
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {actorName}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {formatDate(decision.decidedAt)}
            </span>
            {decision.decisionKind === 'auto' && decision.source && (
              <span className="text-[var(--text-ter)] flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                Source: {decision.source.replace(/_/g, ' ')}
              </span>
            )}
          </div>

          {/* Related objects */}
          {resolvedObjects.length > 0 && (
            <div>
              <div className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mb-1">Related</div>
              <div className="flex flex-wrap gap-1.5">
                {resolvedObjects.map((ro) => {
                  const Icon = KIND_ICON[ro.kind];
                  return (
                    <Link
                      key={`${ro.kind}:${ro.id}`}
                      to={ro.href}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--bg-raised)] hover:bg-[var(--slate-dim)] text-[var(--text-ter)] text-xs border border-[var(--slate)] transition-colors"
                      title={`${DECISION_RELATED_OBJECT_KIND_LABELS[ro.kind]}: ${ro.name}`}
                    >
                      <Icon className="h-3 w-3 opacity-70" />
                      {ro.name}
                      <ChevronRight className="h-3 w-3 opacity-50" />
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tags */}
          {decision.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {decision.tags.map((tag) => (
                <span key={tag} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-[var(--hover-tint)] text-[var(--text-ter)] text-xs rounded">
                  <Tag className="h-2.5 w-2.5" />
                  {tag}
                </span>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {/* A decision has its own page; the row that summarizes it says
                so, rather than leaving the page reachable only by its link. */}
            <Link
              to={decisionPagePath(decision.id)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[var(--text-pri)] bg-[var(--bg-raised)] border border-[var(--text-pri)] hover:bg-[var(--surface)] transition-colors"
            >
              Open this decision&apos;s page
            </Link>
            <button
              type="button"
              onClick={() => onCopyLink(decision)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors"
            >
              <Copy className="h-3 w-3" />
              Copy link
            </button>
            {isContentEditable(decision) ? (
              <button
                type="button"
                onClick={() => onEdit(decision)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--text-ter)] bg-[var(--bg-raised)] border border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-colors"
              >
                <Edit2 className="h-3 w-3" />
                Edit this decision
              </button>
            ) : (
              <p className="text-xs text-[var(--text-ter)]">
                Memorialized — content is immutable. Revise by superseding (row menu).
              </p>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

// ============================================================================
// Decision class badge
// ============================================================================

function ClassBadge({ cls }: { cls: DecisionClass }) {
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium ${DECISION_CLASS_COLORS[cls]}`}>
      <Boxes className="h-3 w-3 opacity-70" />
      {DECISION_CLASS_LABELS[cls]}
    </span>
  );
}

// ============================================================================
// Main page
// ============================================================================

export default function DecisionsPage() {
  const navigate = useNavigate();
  const pageAgents = useMemo(() => [stakeholderPulseAgent(navigate)], [navigate]);
  const allDecisions   = useDecisionsStore((s) => s.decisions);
  const syncState      = useDecisionsStore((s) => s.syncState);
  const fetchDecisions = useDecisionsStore((s) => s.fetchDecisions);
  const createDecision = useDecisionsStore((s) => s.createDecision);
  const updateDecision = useDecisionsStore((s) => s.updateDecision);
  const deleteDecision = useDecisionsStore((s) => s.deleteDecision);
  const addNotification = useUIStore((s) => s.addNotification);

  // Space-aware decision scoping (mirrors sessions/items pattern)
  const currentSpaceId = useSpaceStore((s) => s.currentSpaceId);
  const mySpaces       = useSpaceStore((s) => s.mySpaces);
  const currentDecisionSpace = mySpaces.find((s) => s.id === currentSpaceId) ?? null;
  const decisionSpaceType = currentDecisionSpace?.spaceType ?? null;
  const isDecisionMandateSpace =
    (currentDecisionSpace?.settings as Record<string, unknown> | undefined)?.['originModule'] === 'mandate';
  const currentUserId = useUserStore((s) => s.userId);

  // Apply bounded-context filter before any other filtering
  const decisions = useMemo(() => {
    if (isDecisionMandateSpace) {
      // Mandate space: only mandate-origin decisions
      return allDecisions.filter((d) => d.originModule === 'mandate');
    }
    // All non-mandate spaces: exclude mandate decisions
    const nonMandate = allDecisions.filter((d) => d.originModule !== 'mandate');
    if (decisionSpaceType === 'personal') {
      // Personal space: decisions you personally recorded (decidedBy or createdBy)
      return nonMandate.filter(
        (d) =>
          d.decidedBy === currentUserId ||
          (d as Decision & { createdBy?: string }).createdBy === currentUserId
      );
    }
    // Org / team space: all workspace decisions
    return nonMandate;
  }, [allDecisions, isDecisionMandateSpace, decisionSpaceType, currentUserId]);

  const actorIds = useMemo(
    () => [...new Set(decisions.map((d) => d.decidedBy).filter((id): id is string => !!id))],
    [decisions],
  );
  const actorDisplayMap = useActorDisplayMap(actorIds);
  const resolvedObjectsMap = useResolvedObjects(decisions);

  const [showCreate, setShowCreate] = useState(false);
  const [editingDecision, setEditingDecision] = useState<Decision | null>(null);
  const [supersedingDecision, setSupersedingDecision] = useState<Decision | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [rationalePanelDecision, setRationalePanelDecision] = useState<Decision | null>(null);

  // View mode + selection, URL-addressable (?view=map&selected=<id>) so a
  // decision's map view is a shareable deep link.
  const [searchParams, setSearchParams] = useSearchParams();
  const view: 'table' | 'map' = searchParams.get('view') === 'map' ? 'map' : 'table';
  const mapSelectedId = searchParams.get('selected');
  const setView = useCallback((next: 'table' | 'map') => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === 'map') params.set('view', 'map');
      else { params.delete('view'); params.delete('selected'); }
      return params;
    }, { replace: true });
  }, [setSearchParams]);
  const setMapSelectedId = useCallback((id: string | null) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (id) params.set('selected', id);
      else params.delete('selected');
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<'all' | 'manual' | 'auto'>('all');
  const [impactFilter, setImpactFilter] = useState<DecisionImpact | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<Decision['status'] | 'all'>('active');
  const [classFilter, setClassFilter] = useState<DecisionClass | 'all'>('all');
  const [healthFilter, setHealthFilter] = useState<DecisionHealthStatus | 'all'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('decidedAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => {
    fetchDecisions();
  }, [fetchDecisions]);

  // Deep link from a finding's "Record a decision" action:
  // /prioritization/decisions?attachItem=<itemId> opens the create slide-over
  // with that item pre-attached as a related object. Captured once on mount
  // (lazy initializer) so clearing the URL param below doesn't lose it.
  const [prefillAttachItemId] = useState<string | null>(() => searchParams.get('attachItem'));
  useEffect(() => {
    if (!prefillAttachItemId) return;
    setShowCreate(true);
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete('attachItem');
      return params;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = useCallback((key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  }, [sortKey]);

  const filtered = useMemo(() => {
    let items = decisions;

    if (kindFilter !== 'all')   items = items.filter((d) => d.decisionKind === kindFilter);
    if (impactFilter !== 'all') items = items.filter((d) => d.impact === impactFilter);
    if (statusFilter !== 'all') items = items.filter((d) => d.status === statusFilter);
    if (classFilter !== 'all')  items = items.filter((d) => d.decisionClass === classFilter);
    if (healthFilter !== 'all') items = items.filter((d) => d.healthStatus === healthFilter);

    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(
        (d) =>
          d.title.toLowerCase().includes(q) ||
          (d.body ?? '').toLowerCase().includes(q) ||
          d.tags.some((t) => t.toLowerCase().includes(q)) ||
          (resolvedObjectsMap[d.id] ?? []).some((ro) => ro.name.toLowerCase().includes(q))
      );
    }

    return sortDecisions(items, sortKey, sortDir);
  }, [decisions, kindFilter, impactFilter, statusFilter, classFilter, healthFilter, search, sortKey, sortDir, resolvedObjectsMap]);

  // These handlers THROW on failure. The slide-over catches, keeps itself open
  // with everything the user typed, and shows the reason — it only closes on a
  // real save.
  const lastStoreError = () => useDecisionsStore.getState().lastError;

  const handleCreate = async (
    payload: CreateDecisionPayload,
    alternatives?: { kind: ComparisonSetKind; id: string },
  ) => {
    const created = await createDecision(payload);
    if (!created) {
      throw new Error(`The decision was not saved. ${lastStoreError() ?? 'Try again.'}`);
    }
    addNotification({ type: 'success', title: 'Decision recorded', message: created.title, duration: 3000 });

    // Best-effort: the decision is already saved, so a failure here is
    // reported but never rolled back (canon 27 §6 — alternatives are
    // provenance captured against an existing decision, not part of it).
    if (alternatives) {
      const result = await recordRejectedAlternatives({
        decisionId: created.id,
        comparisonSetKind: alternatives.kind,
        comparisonSetId: alternatives.id,
      });
      if (!result.ok) {
        addNotification({ type: 'error', title: 'Alternatives were not recorded', message: result.error ?? 'Try again from the decision.', duration: 6000 });
      }
    }
  };

  // Supersession (canon 27 §6): the new decision carries the link; the old
  // one transitions to superseded — it is never edited or removed. One
  // governed write (record_decision, mig 765) does both, all or nothing.
  const handleSupersedeCreate = async (payload: CreateDecisionPayload) => {
    const created = await createDecision(payload);
    if (!created) {
      throw new Error(`The new decision was not saved. ${lastStoreError() ?? 'Try again.'}`);
    }
    if (!supersedingDecision) return;
    addNotification({
      type: 'success',
      title: 'Decision superseded',
      message: `“${supersedingDecision.title}” is now marked superseded.`,
      duration: 4000,
    });
  };

  const handleUpdate = async (id: string, payload: UpdateDecisionPayload) => {
    const updated = await updateDecision(id, payload);
    if (!updated) {
      throw new Error(`Your changes were not saved. ${lastStoreError() ?? 'Try again.'}`);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this decision? This cannot be undone.')) return;
    const ok = await deleteDecision(id);
    if (!ok) {
      addNotification({
        type: 'error',
        title: 'Decision was not deleted',
        message: lastStoreError() ?? 'Try again.',
        duration: 7000,
      });
      return;
    }
    if (expandedId === id) setExpandedId(null);
  };

  const handleEdit = (d: Decision) => {
    setEditingDecision(d);
  };

  const handleCopyLink = useCallback(async (decision: Decision) => {
    try {
      await navigator.clipboard.writeText(decisionShareUrl(decision.id));
      addNotification({
        type: 'success',
        title: 'Link copied',
        message: 'Decision link copied to clipboard.',
        duration: 2000,
      });
    } catch {
      addNotification({
        type: 'error',
        title: 'Copy failed',
        message: 'Could not copy the decision link.',
        duration: 4000,
      });
    }
  }, [addNotification]);

  const toggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const counts = useMemo(() => ({
    total:  decisions.length,
    manual: decisions.filter((d) => d.decisionKind === 'manual').length,
    auto:   decisions.filter((d) => d.decisionKind === 'auto').length,
    high:   decisions.filter((d) => d.impact === 'high').length,
  }), [decisions]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <PageHeader
        icon={<GitBranch className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Decisions"
        subtitle="Record and track significant decisions made in the workspace"
        actions={
          <div className="flex items-center gap-2">
            <div className="flex border border-[var(--rule)] overflow-hidden" role="group" aria-label="View mode">
              <button
                type="button"
                onClick={() => setView('table')}
                title="Table view"
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${
                  view === 'table' ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' : 'bg-[var(--bg-raised)] text-[var(--text-ter)] hover:bg-[var(--surface)]'
                }`}
              >
                <List className="h-4 w-4" />
                Table
              </button>
              <button
                type="button"
                onClick={() => setView('map')}
                title="Map view — hierarchy, supersession, and lineage"
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-l border-[var(--rule)] transition-colors ${
                  view === 'map' ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' : 'bg-[var(--bg-raised)] text-[var(--text-ter)] hover:bg-[var(--surface)]'
                }`}
              >
                <Network className="h-4 w-4" />
                Map
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white text-sm font-medium hover:bg-[var(--slate)] transition-colors"
            >
              <Plus className="h-4 w-4" />
              Record decision
            </button>
          </div>
        }
      />

      {/* Agent bar — agents relevant to recorded decisions / outcomes */}
      <AgentBar agents={pageAgents} variant="inline" />

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total',       value: counts.total,  color: 'text-[var(--text-pri)]' },
          { label: 'Manual',      value: counts.manual, color: 'text-[var(--text-ter)]' },
          { label: 'Auto',        value: counts.auto,   color: 'text-[var(--text-ter)]' },
          { label: 'High impact', value: counts.high,   color: 'text-[var(--coral)]' },
        ].map((stat) => (
          <div key={stat.label} className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
            <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
            <div className="text-xs text-[var(--text-ter)] mt-0.5">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Map view */}
      {view === 'map' && (
        <DecisionMapView
          decisions={decisions}
          actorDisplayMap={actorDisplayMap}
          resolvedObjects={resolvedObjectsMap}
          selectedId={mapSelectedId}
          onSelect={setMapSelectedId}
          onEdit={handleEdit}
        />
      )}

      {/* Filters + search */}
      {view === 'table' && (
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4 flex flex-col gap-3">
        {/* Row 1: search + kind */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search decisions…"
              className="w-full pl-9 pr-4 py-2 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-[var(--slate)]"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-ter)] hover:text-[var(--text-sec)]">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="flex gap-1.5">
            {(['all', 'manual', 'auto'] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setKindFilter(k)}
                className={`px-3 py-1.5  text-xs font-medium capitalize transition-colors ${
                  kindFilter === k ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' : 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
                }`}
              >
                {k === 'all' ? 'All kinds' : k === 'manual' ? 'Manual' : (
                  <span className="flex items-center gap-1"><Sparkles className="h-3 w-3" />Auto</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Row 2: class + impact + status */}
        <div className="flex flex-wrap gap-2">
          <select
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value as DecisionClass | 'all')}
            className="px-3 py-2 border border-[var(--rule)] text-sm bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <option value="all">Any class</option>
            {DECISION_CLASS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <select
            value={impactFilter}
            onChange={(e) => setImpactFilter(e.target.value as DecisionImpact | 'all')}
            className="px-3 py-2 border border-[var(--rule)] text-sm bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <option value="all">Any impact</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as Decision['status'] | 'all')}
            className="px-3 py-2 border border-[var(--rule)] text-sm bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <option value="all">Any status</option>
            <option value="active">Active</option>
            <option value="superseded">Superseded</option>
            <option value="reversed">Reversed</option>
          </select>

          <select
            value={healthFilter}
            onChange={(e) => setHealthFilter(e.target.value as DecisionHealthStatus | 'all')}
            className="px-3 py-2 border border-[var(--rule)] text-sm bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <option value="all">Any health</option>
            {(Object.keys(HEALTH_CONFIG) as DecisionHealthStatus[]).map((h) => (
              <option key={h} value={h}>{HEALTH_CONFIG[h].label}</option>
            ))}
          </select>
        </div>
      </div>
      )}

      {/* Table */}
      {view === 'table' && (
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
        {syncState.isLoading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin h-6 w-6 border-2 border-[var(--slate)] border-t-transparent rounded-full" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-4">
            <GitBranch className="h-10 w-10 text-[var(--text-ter)] mb-3" />
            <p className="text-[var(--text-ter)] font-medium">
              {decisions.length === 0 ? 'No decisions recorded yet' : 'No decisions match your filters'}
            </p>
            <p className="text-sm text-[var(--text-ter)] mt-1">
              {decisions.length === 0
                ? 'Click "Record decision" to document a significant choice, or publish a session to auto-record one.'
                : 'Try adjusting your search or filters.'}
            </p>
            {decisions.length === 0 && (
              <button
                type="button"
                onClick={() => setShowCreate(true)}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white text-sm font-medium hover:bg-[var(--slate)] transition-colors"
              >
                <Plus className="h-4 w-4" />
                Record first decision
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--rule)] bg-[var(--surface)]">
                  <th className="px-4 py-3 w-5" />
                  <th className="px-4 py-3 text-left w-full">
                    <SortableHeader label="Title" sortKey="title" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                  </th>
                  <th className="px-4 py-3 text-left whitespace-nowrap">
                    <SortableHeader label="Kind" sortKey="decisionKind" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                  </th>
                  <th className="px-4 py-3 text-left whitespace-nowrap hidden lg:table-cell">Class</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap">
                    <SortableHeader label="Impact" sortKey="impact" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                  </th>
                  <th className="px-4 py-3 text-left whitespace-nowrap">
                    <SortableHeader label="Status" sortKey="status" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                  </th>
                  <th className="px-4 py-3 text-left whitespace-nowrap">
                    <SortableHeader label="Date" sortKey="decidedAt" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                  </th>
                  <th className="px-4 py-3 w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--rule)]">
                {filtered.map((decision) => {
                  const impactCfg   = IMPACT_CONFIG[decision.impact];
                  const statusCfg   = STATUS_CONFIG[decision.status];
                  const actorName   = decision.decidedBy
                    ? (actorDisplayMap[decision.decidedBy] ?? decision.decidedByName ?? null)
                    : (decision.decidedByName ?? null);
                  const relatedObjs = resolvedObjectsMap[decision.id] ?? [];
                  const isExpanded  = expandedId === decision.id;

                  return (
                    <Fragment key={decision.id}>
                      <tr
                        onClick={() => toggleExpand(decision.id)}
                        className={`transition-colors group cursor-pointer ${
                          isExpanded ? 'bg-[var(--slate-dim)]/30' : 'hover:bg-[var(--surface)]/50'
                        }`}
                      >
                        {/* Expand chevron */}
                        <td className="px-4 py-3 w-5">
                          <ChevronRight
                            className={`h-3.5 w-3.5 text-[var(--text-ter)] transition-transform ${isExpanded ? 'rotate-90 text-[var(--text-ter)]' : ''}`}
                          />
                        </td>

                        {/* Title + pills */}
                        <td className="px-4 py-3">
                          <div className="font-medium text-[var(--text-pri)] leading-snug">{decision.title}</div>
                          {/* The Class column is drawn from lg up; below it the class reads here (audit #43). */}
                          {decision.decisionClass && (
                            <div className="lg:hidden mt-1">
                              <ClassBadge cls={decision.decisionClass} />
                            </div>
                          )}
                          {decision.body && !isExpanded && (
                            <div className="text-xs text-[var(--text-ter)] mt-0.5 line-clamp-1 max-w-md">{decision.body}</div>
                          )}
                          {/* Related object pills (compact) */}
                          {relatedObjs.length > 0 && !isExpanded && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {relatedObjs.slice(0, 3).map((ro) => {
                                const Icon = KIND_ICON[ro.kind];
                                return (
                                  <Link
                                    key={`${ro.kind}:${ro.id}`}
                                    to={ro.href}
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[var(--slate-dim)] hover:bg-[var(--slate-dim)] text-[var(--text-sec)] text-xs rounded border border-[var(--slate)] transition-colors max-w-[160px] truncate"
                                  >
                                    <Icon className="h-3 w-3 flex-shrink-0 opacity-70" />
                                    <span className="truncate">{ro.name}</span>
                                  </Link>
                                );
                              })}
                              {relatedObjs.length > 3 && (
                                <span className="text-xs text-[var(--text-ter)] self-center">+{relatedObjs.length - 3}</span>
                              )}
                            </div>
                          )}
                          {/* Tags (compact) */}
                          {decision.tags.length > 0 && !isExpanded && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {decision.tags.slice(0, 3).map((tag) => (
                                <span key={tag} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-[var(--hover-tint)] text-[var(--text-ter)] text-xs rounded">
                                  <Tag className="h-2.5 w-2.5" />{tag}
                                </span>
                              ))}
                              {decision.tags.length > 3 && (
                                <span className="text-xs text-[var(--text-ter)]">+{decision.tags.length - 3}</span>
                              )}
                            </div>
                          )}
                        </td>

                        {/* Kind */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          {decision.decisionKind === 'manual' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--slate-dim)] text-[var(--text-sec)]">
                              <User className="h-3 w-3" />Manual
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--slate-dim)] text-[var(--text-sec)]">
                              <Sparkles className="h-3 w-3" />Auto
                            </span>
                          )}
                        </td>

                        {/* Class */}
                        <td className="px-4 py-3 whitespace-nowrap hidden lg:table-cell">
                          {decision.decisionClass ? (
                            <ClassBadge cls={decision.decisionClass} />
                          ) : (
                            <span className="text-xs text-[var(--text-ter)]">—</span>
                          )}
                        </td>

                        {/* Impact */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${impactCfg.color}`}>
                            {impactCfg.label}
                          </span>
                        </td>

                        {/* Status + Health */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex flex-wrap gap-1">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.color}`}>
                              {statusCfg.label}
                            </span>
                            {decision.healthStatus && (
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${HEALTH_CONFIG[decision.healthStatus].color}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${HEALTH_CONFIG[decision.healthStatus].dot}`} />
                                {HEALTH_CONFIG[decision.healthStatus].label}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Date + actor */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-[var(--text-sec)] text-xs flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-[var(--text-ter)]" />
                            {relativeTime(decision.decidedAt)}
                          </div>
                          {actorName && (
                            <div className="text-[var(--text-ter)] text-xs mt-0.5 flex items-center gap-1">
                              <User className="h-3 w-3" />{actorName}
                            </div>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              title="Copy link to this decision"
                              onClick={(e) => { e.stopPropagation(); handleCopyLink(decision); }}
                              className="p-1 rounded text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:bg-[var(--slate-dim)] transition-colors"
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              title="View rationale"
                              onClick={(e) => { e.stopPropagation(); setRationalePanelDecision(decision); }}
                              className="p-1 rounded text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:bg-[var(--slate-dim)] transition-colors"
                            >
                              <BookOpen className="h-4 w-4" />
                            </button>
                            <RowMenu
                              decision={decision}
                              onDelete={handleDelete}
                              onEdit={handleEdit}
                              onSupersede={setSupersedingDecision}
                              onCopyLink={handleCopyLink}
                            />
                          </div>
                        </td>
                      </tr>

                      {/* Expanded detail row */}
                      {isExpanded && (
                        <ExpandedRow
                          key={`expanded-${decision.id}`}
                          decision={decision}
                          resolvedObjects={relatedObjs}
                          actorName={actorName}
                          onEdit={handleEdit}
                          onCopyLink={handleCopyLink}
                        />
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {filtered.length > 0 && (
          <div className="border-t border-[var(--rule)] px-4 py-2.5 bg-[var(--surface)] text-xs text-[var(--text-ter)]">
            Showing {filtered.length} of {decisions.length} decision{decisions.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>
      )}

      {/* Create slide-over */}
      <DecisionSlideOver
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreate={handleCreate}
        initialRelatedObjects={prefillAttachItemId ? [{ kind: 'item', id: prefillAttachItemId }] : undefined}
      />

      {/* Edit slide-over */}
      <DecisionSlideOver
        open={!!editingDecision}
        onClose={() => setEditingDecision(null)}
        editing={editingDecision}
        onUpdate={handleUpdate}
      />

      {/* Supersede slide-over — new decision replaces the selected one */}
      <DecisionSlideOver
        open={!!supersedingDecision}
        onClose={() => setSupersedingDecision(null)}
        superseding={supersedingDecision}
        onCreate={handleSupersedeCreate}
      />

      {/* Rationale panel */}
      <RationalePanel
        open={!!rationalePanelDecision}
        onClose={() => setRationalePanelDecision(null)}
        decision={rationalePanelDecision}
      />
    </div>
  );
}
